import { ISSUED_PROPERTY_CODE_SOURCE } from "../../supabase/functions/_shared/issued-property-code";

const SENSITIVE_TELEMETRY_KEY =
  /(?:access[_-]?code|session[_-]?token|guest[_-]?token|authorization)/i;

const DIRECT_PROPERTY_IDENTIFIER_KEYS = new Set([
  "title",
  "pagetitle",
  "documenttitle",
  "referrer",
  "pagereferrer",
  "address",
  "propertyaddress",
  "situsaddress",
  "originaladdress",
  "propertyid",
  "manualpropertyid",
  "accountnumber",
  "hcadaccountnumber",
  "parcelid",
  "parcelnumber",
  "apn",
  "ownername",
  "name",
  "fullname",
  "firstname",
  "lastname",
  "email",
  "emailaddress",
  "phone",
  "phonenumber",
  "documentcontent",
  "documenttext",
  "street",
  "unit",
  "city",
  "county",
  "state",
  "zip",
  "zipcode",
  "country",
  "lat",
  "lng",
  "latitude",
  "longitude",
  "addressprovider",
  "placeid",
  "providerplaceid",
  "mailcode",
  "mailcodeid",
  "qrcode",
  "qrcodeid",
  "directmailcode",
  "postcardcode",
  "propertycode",
  "propertycodeid",
]);

const SENSITIVE_QUERY_PARAMETER =
  /([?&](?:access[_-]?code|session[_-]?token|resume|address|street|unit|city|county|state|zip|country|lat(?:itude)?|lng|longitude|address[_-]?provider|place[_-]?id|provider[_-]?place[_-]?id|property[_-]?address|situs[_-]?address|original[_-]?address|property[_-]?id|manual[_-]?property[_-]?id|account[_-]?number|hcad[_-]?account[_-]?number|parcel[_-]?(?:id|number)|apn|owner[_-]?name|email(?:[_-]?address)?|phone(?:[_-]?number)?|document[_-]?(?:content|text)|mail[_-]?code(?:[_-]?id)?|qr[_-]?code(?:[_-]?id)?|direct[_-]?mail[_-]?code|postcard[_-]?code|property[_-]?code(?:[_-]?id)?|code)=)[^&#\s]*/gi;

const DIRECT_MAIL_ROUTE_IDENTIFIER =
  /(\/(?:a|check|qr|go|property)\/)[^/?#\s]+/gi;

const ISSUED_PROPERTY_CODE_IDENTIFIER = new RegExp(`^(?:${ISSUED_PROPERTY_CODE_SOURCE}|DEMO)$`, "i");

// The shared roster includes every state and DC, including codes in generic text.
const ISSUED_PROPERTY_CODE_IN_TEXT = new RegExp(`\\b${ISSUED_PROPERTY_CODE_SOURCE}\\b`, "gi");

const SHARED_ANALYTICS_IDENTIFIERS = new Set([
  "browser-analytics-anonymous",
  "direct-mail-anonymous",
]);

const TELEMETRY_SESSION_ID_STORAGE_KEY =
  "fairpath_analytics_session_pseudonym";

const UUID_TELEMETRY_ID = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

/** Current PostHog and auth identities are UUIDs; unknown legacy IDs stay private. */
export function isSafeBrowserTelemetryIdentity(value: unknown): value is string {
  return typeof value === "string" && (
    UUID_TELEMETRY_ID.test(value) ||
    (value.startsWith("telemetry-session-") && (
      UUID_TELEMETRY_ID.test(value.slice("telemetry-session-".length)) ||
      /^telemetry-session-[a-z0-9]+-[a-z0-9]+$/.test(value)
    ))
  );
}

function isDirectPropertyIdentifierKey(key: string): boolean {
  return DIRECT_PROPERTY_IDENTIFIER_KEYS.has(
    key.replace(/[^a-z0-9]/gi, "").toLowerCase(),
  );
}

export function isIssuedPropertyCodeIdentifier(value: unknown): boolean {
  return typeof value === "string" &&
    ISSUED_PROPERTY_CODE_IDENTIFIER.test(value.trim());
}

/**
 * Keeps immediate and final-boundary browser events pseudonymous when an old
 * SDK identity is a property code or a shared fallback value.
 */
export function safeBrowserTelemetryDistinctId(candidate: unknown): string {
  if (isSafeBrowserTelemetryIdentity(candidate)) {
    return candidate;
  }

  if (typeof window !== "undefined") {
    try {
      const stored = window.sessionStorage?.getItem(
        TELEMETRY_SESSION_ID_STORAGE_KEY,
      );
      if (isSafeBrowserTelemetryIdentity(stored)) return stored;
    } catch {
      // Storage can be unavailable in hardened/private browser contexts.
    }
  }

  const cryptoApi = typeof window !== "undefined"
    ? window.crypto
    : globalThis.crypto;
  const randomPart = cryptoApi?.randomUUID?.() ??
    `${Date.now().toString(36)}-${Math.random().toString(36).slice(2)}`;
  const generated = `telemetry-session-${randomPart}`;

  if (typeof window !== "undefined") {
    try {
      window.sessionStorage?.setItem(
        TELEMETRY_SESSION_ID_STORAGE_KEY,
        generated,
      );
    } catch {
      // A per-event pseudonym remains safer than reusing a property code.
    }
  }

  return generated;
}

/** Redacts credentials and direct property identity at every browser telemetry boundary. */
export function sanitizeTelemetryValue(value: unknown, key = ""): unknown {
  if (
    SENSITIVE_TELEMETRY_KEY.test(key) ||
    isDirectPropertyIdentifierKey(key)
  ) {
    return "[Filtered]";
  }
  if (typeof value === "string") {
    if (isIssuedPropertyCodeIdentifier(value) || SHARED_ANALYTICS_IDENTIFIERS.has(value)) {
      return "[Filtered]";
    }
    return value
      .replace(SENSITIVE_QUERY_PARAMETER, "$1[Filtered]")
      .replace(DIRECT_MAIL_ROUTE_IDENTIFIER, "$1[Filtered]")
      .replace(ISSUED_PROPERTY_CODE_IN_TEXT, "[Filtered]");
  }
  if (Array.isArray(value)) {
    return value.map((item) => sanitizeTelemetryValue(item));
  }
  // A Date carries no identity and has no own keys, so rebuilding it below
  // would yield `{}`. PostHog rejects a request whose event timestamp is an
  // object, which silently dropped every unbatched $pageview and $pageleave.
  if (value instanceof Date) {
    return value;
  }
  if (value && typeof value === "object") {
    return Object.fromEntries(
      Object.entries(value as Record<string, unknown>).map(([childKey, child]) => [
        childKey,
        sanitizeTelemetryValue(child, childKey),
      ]),
    );
  }
  return value;
}

export function sanitizeTelemetryUrl(url: string): string {
  return sanitizeTelemetryValue(url) as string;
}

export function hasSensitiveTelemetryLocation(url: string): boolean {
  if (sanitizeTelemetryUrl(url) !== url) return true;
  try {
    // Property checks remain private after their identity moves out of the URL.
    const pathname = new URL(url, "https://fairpath.app").pathname;
    return pathname === "/check" || pathname === "/dashboard";
  } catch {
    return false;
  }
}
